// Simplified error classes for the Airbolt SDK

/**
 * Base error class for all Airbolt SDK errors
 */
export class AirboltError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
  ) {
    super(message);
    this.name = "AirboltError";
    // Maintain proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AirboltError);
    }
  }
}

/**
 * Network and connectivity errors - these are always retryable
 */
export class AirboltNetworkError extends AirboltError {
  public retryable = true;

  constructor(
    message: string,
    public originalError?: Error,
  ) {
    super(message, "NETWORK_ERROR");
    this.name = "AirboltNetworkError";
  }
}

/**
 * Factory function to create appropriate error from HTTP response
 */
export function createErrorFromResponse(
  response: Response,
  responseText?: string,
): AirboltError {
  const { status, statusText } = response;
  const message = responseText || statusText || "Unknown error";

  // Use statusCode to determine error type and retryability
  let code: string;
  let errorMessage: string;

  if (status === 401) {
    code = "AUTH_ERROR";
    errorMessage = "Unauthorized: Invalid project ID or missing credentials";
  } else if (status === 403) {
    code = "AUTH_ERROR";
    errorMessage = "Forbidden: Access denied";
  } else if (status === 400) {
    code = "VALIDATION_ERROR";
    errorMessage = `Bad Request: ${message}`;
  } else if (status === 429) {
    code = "RATE_LIMIT_ERROR";
    errorMessage = "Rate limit exceeded";
  } else if (status >= 400 && status < 500) {
    code = "CLIENT_ERROR";
    errorMessage = `Client error (${status}): ${message}`;
  } else if (status >= 500) {
    code = "SERVER_ERROR";
    errorMessage = `Server error (${status}): ${message}`;
  } else {
    code = "HTTP_ERROR";
    errorMessage = `HTTP error (${status}): ${message}`;
  }

  return new AirboltError(errorMessage, code, status);
}

/**
 * Factory function to create appropriate error from network failure
 */
export function createNetworkError(originalError: Error): AirboltNetworkError {
  // Detect timeout errors
  if (
    originalError.name === "AbortError" ||
    /\btimeout|ETIMEDOUT\b/i.test(originalError.message)
  ) {
    return new AirboltNetworkError("Request timeout", originalError);
  }

  // Network connectivity issues
  if (
    /\bfetch|network|ENOTFOUND|ECONNREFUSED|ECONNRESET|EAI_AGAIN|TLS|SSL|certificate\b/i.test(
      originalError.message,
    )
  ) {
    return new AirboltNetworkError(
      "Network error: Unable to connect to server",
      originalError,
    );
  }

  // Generic network error
  return new AirboltNetworkError(
    `Network error: ${originalError.message}`,
    originalError,
  );
}

/**
 * Type guard to check if an error is retryable
 */
export function isRetryableError(error: Error): boolean {
  // Network errors are always retryable
  if (error instanceof AirboltNetworkError) {
    return true;
  }

  // Server errors (5xx) and rate limits (429) are retryable
  if (error instanceof AirboltError) {
    return (
      error.statusCode === 429 || // Rate limiting
      (error.statusCode !== undefined && error.statusCode >= 500) // Server errors
    );
  }

  return false;
}

/**
 * Type guard to check if an error is an Airbolt SDK error
 */
export function isAirboltError(error: unknown): error is AirboltError {
  return error instanceof AirboltError;
}
